import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfileContactSection
} from './StaffProfileTemplate';

const LesterLaCombeProfile = () => {
  const person = {
    id: "lester-lacombe",
    name: "Lester LaCombe",
    honorific: null,
    title: "Independent Researcher, Systems Theorist, VR Developer",
    affiliation: "Founder, Home Planet Digital",
    image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/z3cjv2kh_Lester%20Lacombe.jpg",
    email: "homeplanetdigital@gmail.com",
    category: "Fellows & Visiting Scholars",
    testid: "lester-lacombe-profile"
  };

  return (
    <StaffProfileTemplate person={person}>
      {/* Intellectual Biography */}
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            "Lester LaCombe is an independent researcher, systems theorist, VR developer, and founder whose work explores endogenous neurochemistry, fungal disease, cancer biology, immune dysregulation, altered states, anomalous experience, and immersive sensory systems. His research is organized around hidden biological architectures: the distributed molecular, microbial, inflammatory, perceptual, and environmental systems that may shape health, cognition, transformation, and extraordinary states of consciousness.",
            "His primary medical work focuses on endogenous dimethyltryptamine, trace amine biology, fungal extracellular vesicles, cancer initiation, apoptosis signaling, Long COVID, immune persistence, and Sigma-1 receptor-linked cellular regulation. Across these areas, Lester develops systems level models that examine how enzymatic gates, inflammatory states, microbial products, mitochondrial stress responses, clearance pathways, and localized signaling microdomains may interact across tissues.",
            "A central part of his research is a systems level reconsideration of endogenous DMT. Rather than treating DMT primarily as a conventional psychedelic compound or classical neurotransmitter, he frames it as an endogenous trace amine whose biological significance may depend on transient, tissue-specific ensemble states. His model emphasizes tryptophan-derived tryptamine, AADC, INMT, methyl-donor availability, monoamine oxidase clearance, inflammatory compression, pulmonary and enteric relay architectures, choroid plexus and central nervous system compartments, and downstream integration through intracellular systems such as Sigma-1 receptor signaling.",
            "His cancer research focuses on fungal extracellular vesicles and fungal apoptosis proteases as candidate mechanisms in cancer initiation and selective cancer cell vulnerability. This work explores whether fungal vesicle exposure may induce cancer-like cellular states under specific inflammatory or tissue conditions, and whether conserved fungal apoptosis pathways may provide candidate tools for selectively targeting fungal or cancerous cells while sparing healthy tissue.",
            "He is also the creator of Excelsior VR, an immersive sensory modulation platform designed around structured color modes, light rhythm, sound, haptics, meditative states, lucid-state exploration, and potential biofeedback integration. Excelsior functions both as a technology project and as an experimental environment for investigating how organized sensory fields may influence attention, mood, cognition, anomalous perception, and altered states."
          ]}
        />
      </ProfileSection>

      {/* Research Foci & Areas of Work */}
      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileBullets
          intro="Lester's interdisciplinary research spans biomedical theory, consciousness studies, and immersive technology:"
          items={[
            "Endogenous neurochemistry and trace amine biology",
            "Endogenous dimethyltryptamine (DMT) systems-level models",
            "Fungal disease, extracellular vesicles, and immune persistence",
            "Cancer biology, apoptosis signaling, and cancer initiation mechanisms",
            "Long COVID and immune dysregulation",
            "Sigma-1 receptor-linked cellular regulation",
            "Altered states of consciousness and anomalous experience",
            "Tryptophan-derived tryptamine, AADC, INMT, and methylation pathways",
            "Monoamine oxidase clearance, inflammatory compression, and transient microdomains",
            "Pulmonary, enteric, choroid plexus, and central nervous system DMT relay models",
            "Cryptococcus, Candida, Saccharomyces, vomocytosis, and immune persistence",
            "STAT3 signaling, miR-124, and inflammatory lock-in models",
            "VR-based sensory modulation, entrainment, and altered-state interface design",
            "Psi, anomalous perception, dreamlike cognition, and biologically mediated extraordinary experience",
            "Phenomenon research and sensory entrainment systems"
          ]}
        />
      </ProfileSection>

      {/* Research Projects & Outputs */}
      <ProfileSection title="Research Projects & Knowledge Outputs">
        <ProfileParagraphs
          items={[
            "Lester's work bridges biomedical research and immersive technology development. His outputs include biomedical manuscripts, experimental diagrams, mechanistic frameworks, VR architecture documents, sensory protocol designs, AI-assisted research synthesis, public theory development, and investor-facing research materials.",
            "Through Home Planet Digital, he develops interdisciplinary frameworks connecting biomedical theory, consciousness research, immersive technology, anomalous experience, and public scientific speculation. His public work includes long-form theory development, research proposals, biomedical frameworks, VR design documentation, and community-facing explanations of complex systems."
          ]}
        />
      </ProfileSection>

      {/* Public Engagement */}
      <ProfileSection title="Public Engagement & Community Work">
        <ProfileParagraphs
          items={[
            "Alongside his research and development work, Lester is active as a public-facing independent theorist and founder. Through Home Planet Digital, he develops interdisciplinary frameworks connecting biomedical theory, consciousness research, immersive technology, anomalous experience, and public scientific speculation.",
            "He has engaged with audiences interested in fungal disease, cancer theory, Long COVID, altered states, psi research, endogenous DMT, artificial intelligence, and immersive media. His approach reflects a commitment to making complex systems-level thinking accessible while maintaining scientific rigour."
          ]}
        />
      </ProfileSection>

      {/* Contact & Scholarly Infrastructure */}
      <ProfileContactSection
        email="homeplanetdigital@gmail.com"
        role="Highland Fellow; Founder, Home Planet Digital"
        affiliationLines={[
          "Home Planet Digital",
          "Independent Research"
        ]}
        scholarlyProfiles={[]}
        testidPrefix="lester"
      />
    </StaffProfileTemplate>
  );
};

export default LesterLaCombeProfile;
